(ns shared.core.components.recharts.radar-chart
  (:require [shared.core.components.recharts.tooltip :as tooltip]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.reactive :as re]
            [shared.core.style :as style]))

(defn- custom-polar-angle-tick [props]
  (let [{:keys [x y payload textAnchor]} (js->clj props :keywordize-keys true)]
    (re/as-element
      [:text {:x x
              :y y
              :text-anchor textAnchor
              :font-family style/roboto-font-family
              :fill (style/alc-color "Text/primary")
              :font-size 11}
       (:value payload)])))

(defn radar-chart
  "A radar / spider chart supporting one or more named series.

   Data structure — one map per spoke (dimension), shared by all series:
     [{:subject \"Math\" :group-a 120 :group-b 110}
      {:subject \"English\" :group-a 98 :group-b 130}]

   Required:
   - :data       — vector of dimension maps (one entry per spoke)
   - :axis-key   — key whose value labels each spoke (e.g. :subject)

   Series — choose one form:
   - :radars     — vector of series maps for multi-series charts:
                   [{:name \"Group A\" :data-key :group-a :color \"#abc\"}
                    {:name \"Group B\" :data-key :group-b}]
   - :data-key   — shorthand key for a single-series chart (use with :name / :color)

   Optional:
   - :name              — series name for single-series (used in tooltip / legend)
   - :color             — fill color for single-series (auto-generated if omitted)
   - :fill-opacity      — area fill opacity (default 0.25)
   - :outer-radius      — chart radius as % string or px number (default \"75%\")
   - :domain            — PolarRadiusAxis domain, e.g. [0 150] (default [0 \"auto\"])
   - :grid-type         — \"polygon\" | \"circle\" (default \"polygon\")
   - :radius-axis-visible? — show radial value labels (default false)
   - :dot?              — show dots at data points (default false)
   - :tooltip-visible?  — (default true)
   - :show-legend?      — (default false)
   - :legend-label-fn   — (fn [series-name]) for custom label display
   - :legend-config     — extra props merged into standalone-legend
   - :width             — (default \"100%\")
   - :height            — (default 300)"
  [{:keys [data
           axis-key
           data-keys
           fill-opacity
           outer-radius
           domain
           grid-type
           radius-axis-visible?
           dot?
           tooltip-visible?
           show-legend?
           legend-label-fn
           legend-config
           width
           height]
    :or {fill-opacity 0.25
         outer-radius "75%"
         domain [0 "auto"]
         grid-type "polygon"
         radius-axis-visible? false
         dot? false
         tooltip-visible? true
         show-legend? false
         width "100%"
         height 300}}]
  (let [effective-radars (->> data-keys
                              (mapv (fn [dk]
                                      {:name dk
                                       :data-key dk
                                       :fill (recharts.util/color-from-string dk)
                                       :fill-opacity fill-opacity})))
        legend-entries (when show-legend?
                         (mapv (fn [r]
                                 {:label (if legend-label-fn (legend-label-fn (:name r)) (:name r))
                                  :color (recharts.util/color-from-string (:name r))})
                               effective-radars))
        radar-elements (mapv (fn [r]
                               (let [rc (recharts.util/color-from-string (:name r))]
                                 [recharts.util/re-radar
                                  {:name (:name r)
                                   :dataKey (:data-key r)
                                   :stroke rc
                                   :fill rc
                                   :fillOpacity fill-opacity
                                   :dot dot?}]))
                             effective-radars)]
    [:div {:style {:width width :display :flex :flex-direction :column}}
     [recharts.util/re-responsive-container {:width "100%" :height height}
      (into [recharts.util/re-radar-chart {:data data
                                           :outerRadius outer-radius
                                           :margin {:top 10 :right 20 :bottom 10 :left 20}}
             [recharts.util/re-polar-grid {:gridType grid-type
                                           :stroke (style/alc-color "Border/chart_norm")}]
             [recharts.util/re-polar-angle-axis {:dataKey axis-key
                                                 :axisLine {:stroke (style/alc-color "Border/chart_act")}
                                                 :tick custom-polar-angle-tick}]
             (when radius-axis-visible?
               [recharts.util/re-polar-radius-axis {:domain domain
                                                    :tickLine false
                                                    :tick #(recharts.util/custom-y-tick % {})}])
             (when tooltip-visible?
               [recharts.util/re-tooltip
                {:content (tooltip/chart-tooltip-content)}])]
            radar-elements)]
     (when (not-empty legend-entries)
       [recharts.util/standalone-legend (assoc legend-config :entries legend-entries)])]))